import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';

// Timer configuration
const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity
const COUNTDOWN_TIME = 30; // 30 seconds countdown before auto-logout

export const SessionTimeoutModal = () => {
    const [showModal, setShowModal] = useState(false);
    const [timeLeft, setTimeLeft] = useState(COUNTDOWN_TIME);
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // Timer ref hooks
    const idleTimerRef = React.useRef(null);
    const countdownIntervalRef = React.useRef(null);

    const handleLogout = useCallback(async () => {
        clearInterval(countdownIntervalRef.current);
        setShowModal(false);
        if (user) {
            await logout();
            navigate(`${process.env.PUBLIC_URL}/`);
        }
    }, [logout, navigate, user]);

    const resetIdleTimer = useCallback(() => {
        if (showModal) return; // Do not reset timer if the modal is currently awaiting user input
        
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        
        if (user) {
            idleTimerRef.current = setTimeout(() => {
                setShowModal(true);
                setTimeLeft(COUNTDOWN_TIME);
            }, IDLE_TIMEOUT);
        }
    }, [showModal, user]);

    useEffect(() => {
        // Expose a debug function globally so we can trigger it from the console to verify UI
        window.triggerSessionTimeoutDebug = () => {
            setShowModal(true);
            setTimeLeft(COUNTDOWN_TIME);
        };

        // Standard user interactions that reset inactivity timer
        const events = [
            'mousemove', 'keydown', 'wheel', 'mousedown', 'touchstart', 'touchmove'
        ];
        
        const handleUserActivity = () => resetIdleTimer();
        
        events.forEach(event => document.addEventListener(event, handleUserActivity));
        resetIdleTimer(); // Kickstart the timer initially

        return () => {
            events.forEach(event => document.removeEventListener(event, handleUserActivity));
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        };
    }, [resetIdleTimer]);

    useEffect(() => {
        if (showModal) {
            countdownIntervalRef.current = setInterval(() => {
                setTimeLeft((prevTime) => {
                    if (prevTime <= 1) {
                        handleLogout();
                        return 0;
                    }
                    return prevTime - 1;
                });
            }, 1000);
        } else {
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        }

        return () => {
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        };
    }, [showModal, handleLogout]);

    const continueSession = () => {
        setShowModal(false);
        resetIdleTimer();
    };

    if (!user) return null; // Only ever mount tracking mechanics if a user is logged in

    return (
        <Modal show={showModal} onHide={handleLogout} centered backdrop="static" keyboard={false}>
            <Modal.Body className="text-center p-5">
                <div 
                    className="d-inline-flex justify-content-center align-items-center rounded-circle mb-4"
                    style={{ width: '80px', height: '80px', backgroundColor: '#fef0d7' }}
                >
                    <i className="fe fe-clock" style={{ fontSize: '40px', color: '#f7931e' }}></i>
                </div>
                
                <h3 className="fw-bold mb-3" style={{ color: '#333' }}>Sesi Akan Berakhir</h3>
                <p className="text-muted mb-4 px-3" style={{ fontSize: '15px' }}>
                    Sesi Anda telah habis. Apakah Anda ingin melanjutkan sesi?
                </p>

                <div 
                    className="d-flex align-items-center p-3 mb-4 rounded" 
                    style={{ backgroundColor: '#fdf7ea', border: '1px solid #fbe5cf' }}
                >
                    <div 
                        className="rounded-circle text-white d-flex align-items-center justify-content-center fw-bold me-3" 
                        style={{ width: '40px', height: '40px', backgroundColor: '#f7931e', fontSize: '18px' }}
                    >
                        {timeLeft}
                    </div>
                    <span style={{ color: '#8a6632', fontWeight: '500' }}>detik sebelum logout otomatis</span>
                </div>

                <div className="d-grid gap-3">
                    <Button 
                        variant="primary" 
                        size="lg" 
                        onClick={continueSession}
                        className="d-flex align-items-center justify-content-center fw-bold"
                        style={{ backgroundColor: '#0d7df2', border: 'none', borderRadius: '8px', padding: '12px', fontSize: '16px' }}
                    >
                        <i className="fe fe-refresh-cw me-2"></i> Ya, Lanjutkan Sesi
                    </Button>
                    
                    <Button 
                        variant="outline-danger" 
                        size="lg" 
                        onClick={handleLogout}
                        className="d-flex align-items-center justify-content-center fw-bold"
                        style={{ borderRadius: '8px', padding: '12px', color: '#ff4d4f', borderColor: '#ffcdce', backgroundColor: '#ffffff', fontSize: '16px' }}
                    >
                        <i className="fe fe-log-out me-2"></i> Logout
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};
